// components/settings/surfox-settings/sections/phone-numbers/EligibilityGate.jsx
//
// Coach card shown inside the Port In tab when the tenant is not yet eligible
// to port. Renders ALL gate items (not just blocked ones) so the customer sees
// their progress: green checkmarks for what's done, amber for what's left,
// with direct action links per blocker.
//
// `no_dedicated_subaccount` and `on_trial` are collapsed into ONE checklist row
// since they represent the same customer-facing state (you haven't graduated
// onto your own Twilio subaccount yet). Both eligibility helper blockers point
// at the same /settings/a2p-compliance action anyway.
//
// Gate rows shown:
//   - subaccount_status        → finish A2P registration (covers both
//                                no_dedicated_subaccount and on_trial)
//   - no_verified_a2p          → finish A2P registration
//   - feature_disabled         → not shown here (the tab itself is hidden)
//
// Phase F (2026-05-17): docusign_not_configured row removed. Twilio's
// Porting API auto-generates its own LOA so no e-signature integration is
// required on SurFox's side.

import React from 'react';
import { CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';

function getSubaccountRowContent(blockerSet) {
  const noSubaccount = blockerSet.has('no_dedicated_subaccount');
  const onTrial = blockerSet.has('on_trial');
  const blocked = noSubaccount || onTrial;

  let title;
  let body;
  if (noSubaccount) {
    title = 'Set up a dedicated Twilio subaccount';
    body = 'Port-in requires your own dedicated Twilio subaccount. Complete A2P registration to provision one.';
  } else if (onTrial) {
    title = 'Graduate from trial subaccount';
    body = "You're currently on the shared trial Twilio subaccount. Complete A2P registration to move to your own dedicated subaccount.";
  } else {
    title = 'Dedicated Twilio subaccount in place';
    body = 'Your numbers will land on your own Twilio subaccount, not the shared trial.';
  }

  return {
    blocked,
    title,
    body,
    actionLabel: 'Complete A2P registration',
    actionHref: '/settings/a2p-compliance',
  };
}

const CHECKLIST = [
  { key: 'subaccount_status', resolve: (blockerSet) => getSubaccountRowContent(blockerSet) },
  {
    key: 'no_verified_a2p',
    resolve: (blockerSet) => ({
      blocked: blockerSet.has('no_verified_a2p'),
      title: blockerSet.has('no_verified_a2p')
        ? 'Verify A2P brand and campaign'
        : 'A2P brand and campaign verified',
      body: blockerSet.has('no_verified_a2p')
        ? 'Your A2P registration is not yet verified. Ported numbers attach to a verified campaign on day one, so this needs to land first.'
        : 'Your verified A2P campaign is ready to receive ported numbers.',
      actionLabel: 'Open A2P setup',
      actionHref: '/settings/a2p-compliance',
    }),
  },
];

export default function EligibilityGate({ eligibility }) {
  const blockerSet = new Set(eligibility?.blockers ?? []);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-slate-900">Almost there</h3>
        <p className="text-sm text-slate-500 mt-1 max-w-xl mx-auto">
          A few items need to be in place before you can port your existing
          business numbers into SurFox. Once everything is green, the port
          wizard unlocks automatically.
        </p>
      </div>

      <div className="space-y-3 max-w-2xl mx-auto">
        {CHECKLIST.map(({ key, resolve }) => {
          const content = resolve(blockerSet);
          const { blocked, title, body, actionLabel, actionHref } = content;

          return (
            <div
              key={key}
              className={`flex items-start space-x-3 p-4 rounded-xl border ${
                blocked
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-emerald-50 border-emerald-200'
              }`}
            >
              {blocked ? (
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${blocked ? 'text-amber-900' : 'text-emerald-900'}`}>
                  {title}
                </p>
                <p className={`text-sm mt-0.5 ${blocked ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {body}
                </p>
                {blocked && actionHref && (
                  <a
                    href={actionHref}
                    target={actionHref.startsWith('mailto:') ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-2 text-sm font-medium text-amber-800 hover:text-amber-900"
                  >
                    {actionLabel}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-center text-slate-400 mt-6 max-w-md mx-auto">
        Number porting takes 2 to 4 weeks once submitted. Your existing numbers
        stay live throughout the entire process.
      </p>
    </div>
  );
}
